import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, vModelText as _vModelText, withKeys as _withKeys, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "list-name-wrap" }
const _hoisted_2 = ["onKeydown"]
const _hoisted_3 = {
  key: 8,
  class: "cards-count usn"
}
const _hoisted_4 = {
  key: 9,
  class: "cards-count usn"
}
const _hoisted_5 = {
  key: 0,
  class: "board-list-actions-wrap pt"
}

import { ref, computed, nextTick, toRef } from 'vue';
import ToggleExpandButton from '@/components/elements/common/ToggleExpandButton';
import StatusIconPicker from './StatusIconPicker';
import { groupByOption } from '@/types/groupByOption';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import useIsEditable from '@/utilities/composables/useIsEditable';
import { ResourceType } from '@/types/resources';
import useIsBrowser from '@/utilities/composables/useIsBrowser.ts';
import useTranslate from '@/utilities/composables/useTranslate';
import useElementFocus from '@/utilities/composables/useElementFocus.ts';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import BoardListActions from '@/components/widgets/BoardListActions';
import ButtonCustom from '@/components/elements/ButtonCustom';
import ResourceIcon from '@/components/elements/common/ResourceIcon';
import UserAvatar from '@/components/widgets/UserAvatar';
import useGetBoardList from '@/utilities/composables/useGetBoardList';
import { getCardStatus, getDefaultCardStatuses } from '@/utilities/defaultCardStatuses';
import cardStatusType from '@/types/cardStatusType';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import constants from '@/utilities/constants';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import isMobile from '@/utilities/isMobile';


export default /*@__PURE__*/_defineComponent({
  __name: 'BoardListHeader',
  props: {
  listId: {
    type: [String, Number],
    required: true,
  },
  isCardsLayoutTimeline: {
    type: Boolean,
    required: true,
  },
  headerFooterStyle: {
    type: Object,
    required: true,
  },
  expandable: {
    type: Boolean,
    required: false,
    default: false,
  },
  showStatusIconPicker: {
    type: Boolean,
    required: false,
    default: false,
  },
  disableStatusIconPicker: {
    type: Boolean,
    required: false,
    default: false,
  },
  groupBy: {
    type: String,
    required: false,
    default: groupByOption.status,
  },
  listCards: {
    type: Array,
    required: true,
  },
  visible: {
    type: Boolean,
    required: false,
    default: true,
  },
  isEpicBoard: {
    type: Boolean,
    required: false,
    default: false,
  },
},
  emits: ['headerMouseEnter', 'headerMouseOut'],
  setup(__props, { emit: __emit }) {

const props = __props;
const store = useStore();
const route = useRoute();
const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
const { isSafari } = useIsBrowser();
const { translate } = useTranslate();
const { focusElementHandler, blurElementHandler, isFocusElement } = useElementFocus();
const { iconPath, getCustomIconStyle } = useCustomIcon();
const { isEditable } = useIsEditable();

// Convert props to reactive refs
const groupByRef = toRef(props, 'groupBy');
const listIdRef = toRef(props, 'listId');

// Get reactive list from composable
const { list } = useGetBoardList(groupByRef, listIdRef);

const { isGuestReader } = useCurrentRole();
const emit = __emit;
const listTitleFocused = ref(false);
const oldTitle = ref('');
const localTitle = ref('');
const debounceUpdateTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const listTitleInput = ref<HTMLInputElement | null>(null);

const isExpanded = computed(() => {
  return store.getters.getListExpanded(currentBoardId.value, listIdRef.value);
});

const listTitle = computed({
  get() {
    return list.value.title || '';
  },
  set(title) {
    localTitle.value = title.trim();
  },
});

const showListActionsButton = computed(() => {
  return (
    props.visible &&
    (!props.expandable || (props.expandable && isExpanded.value) || actionsDropdownIsShown.value) &&
    !isMobile() &&
    !inEpicBoard &&
    !isGuestReader.value &&
    !subGroupByEnabled.value
  );
});
const isRolledoverList = computed(() => {
  return list.value?.orginalId === constants.rolledOverList;
});
const currentBoardId = computed(() => {
  if (embed) {
    return embeddedBoardAttributes?.value?.boardId;
  }
  return store.getters.currentBoardId;
});
const actionsDropdownIsShown = computed(() => {
  return store.getters.getCurrentListIdActionEdit === listIdRef.value;
});
const groupByAssignee = computed(() => {
  return groupByRef.value === groupByOption.assignee;
});
const groupByStatus = computed(() => {
  return groupByRef.value === groupByOption.status;
});
const groupByEpic = computed(() => {
  return groupByRef.value === groupByOption.epic;
});
const isUnassignedListId = computed(() => {
  return listIdRef.value === `${currentBoardId.value}-unassigned`;
});
const listStatusIcon = computed(() => {
  return (
    (getCardStatus(list.value.behavior, props.isEpicBoard) || {}).icon ||
    list.value.icon ||
    getCardStatus(cardStatusType.Backlog).icon ||
    ''
  );
});

const subGroupByEnabled = computed(() => {
  return store.getters.getSubGroupByEnabled(currentBoardId.value);
});

const listStatusColor = computed(() => {
  const defaultCardStatuses = getDefaultCardStatuses();
  return (
    list.value.color ||
    (defaultCardStatuses[list.value.behavior] || {})?.color ||
    defaultCardStatuses.backlog.color ||
    ''
  );
});
const isSprint = computed(() => {
  return route.name === constants.routeNames.sprint || inboxSprint.value;
});
const inboxSprint = computed(() => {
  return (
    route.name === constants.routeNames.inbox &&
    store.getters.getCurrentInboxResource.resource_type === ResourceType.Sprint
  );
});
const readOnly = computed(() => {
  return embeddedBoardAttributes?.value?.readOnly ?? false;
});

const isInEpicBoard = computed(() => {
  return currentBoardId.value.includes(constants.inEpicBoardIdPrefix);
});

const numberOfCardsTooltipContent = computed(() => {
  const uniqueBoardIds = new Set(
    props.listCards.map((cardId: any) => store.getters.getCards[cardId]?.board_id)
  );
  const numberOfCards = props.listCards?.length;
  const cardTranslation = translate(numberOfCards === 1 ? 'card' : 'cards').toLowerCase();
  const boardsTranslation = translate('boardsCount', {
    count: uniqueBoardIds.size,
  }).toLowerCase();
  const fromPart = uniqueBoardIds.size >= 1 ? ` ${translate('from')} ${boardsTranslation}` : '';

  return `${numberOfCards} ${listTitle.value.toLowerCase()} ${cardTranslation}${fromPart}`;
});

function closeTitleEditInput($event: Event) {
  listTitleFocused.value = false;
  localTitle.value = oldTitle.value;
  updateListProperty('title', oldTitle.value);
  $event.stopPropagation();
  $event.preventDefault();
}
function focusListTitle() {
  if (!groupByStatus.value || readOnly.value || isSprint.value || inEpicBoard) return;
  listTitleFocused.value = true;
  oldTitle.value = list.value.title;
  nextTick(() => {
    if (listTitleInput.value) {
      listTitleInput.value.focus();
      listTitleInput.value.select();
    }
  });
}
function updateListProperty(propertyName: string, value: string) {
  const oldValue = list.value[propertyName];
  const updatedList = { [propertyName]: value };
  store.dispatch('setBoardListProp', { listId: listIdRef.value, propertyName, value });
  updateList(updatedList).catch(() => {
    store.dispatch('setBoardListProp', {
      listId: listIdRef.value,
      propertyName,
      value: oldValue,
    });
  });
}
function updateList(newList: any) {
  return store.dispatch('updateList', {
    boardId: currentBoardId.value,
    listId: listIdRef.value,
    list: newList,
  });
}
const headerMouseEnter = () => {
  emit('headerMouseEnter');
};
const headerMouseOut = () => {
  emit('headerMouseOut');
};
const onUpdateExpanded = (value: boolean) => {
  store.dispatch('setListsExpanded', {
    boardId: currentBoardId.value,
    listId: listIdRef.value,
    value,
  });
};
function handleStatusItemClick(status: any) {
  if (props.listCards.length > 0) {
    const resourceTypeTranslation = translate(props.isEpicBoard ? 'epics' : 'cards').toLowerCase();
    store.dispatch('addUiNotification', {
      message: translate('cannotModidyListStatus', {
        resourceType: resourceTypeTranslation,
      }),
      status: constants.uiNotificationStatuses.error,
      duration: 5000,
    });
    return;
  }
  const newList = {
    behavior: status.id,
    icon: status.icon,
    color: status.color,
  };
  store.dispatch('setBoardList', {
    listId: listIdRef.value,
    list: {
      ...list.value,
      ...newList,
      ...(localTitle.value && { title: localTitle.value }),
    },
  });
  updateList(newList);
  nextTick(() => {
    if (listTitleInput.value) {
      listTitleInput.value.focus();
    }
  });
}
function handleColorSelect(color: string) {
  const oldValue = list.value.color;
  const payload = { color };
  store.dispatch('setBoardListProp', {
    listId: listIdRef.value,
    propertyName: 'color',
    value: color,
  });
  // debounce color update
  if (debounceUpdateTimeout.value) {
    clearTimeout(debounceUpdateTimeout.value);
  }
  debounceUpdateTimeout.value = setTimeout(() => {
    updateList(payload).catch(() => {
      store.dispatch('setBoardListProp', {
        listId: list.value.id,
        propertyName: 'color',
        value: oldValue,
      });
    });
  }, 100);
}
function handleEnterKey(evt: KeyboardEvent) {
  handleInputBlur(evt as unknown as FocusEvent);
}

function handleInputBlur(evt: FocusEvent) {
  if (isFocusElement()) return;
  const classes = ['child-card-status-icon', 'status-icon-dropdown-wrap', 'status-icon-wrap'];
  const statusIconClick = classes.some((c) => (evt?.target as HTMLElement)?.className.includes(c));
  if (statusIconClick) {
    const { listId } = (evt.target as HTMLElement)?.dataset || {};
    if (listId === listIdRef.value) {
      return;
    }
  }
  if ((evt.relatedTarget as HTMLElement)?.id === 'discard-button') {
    listTitleFocused.value = false;
    localTitle.value = list.value.title;
    return;
  }
  if (!localTitle.value) {
    listTitleFocused.value = false;
    return;
  }
  updateListProperty('title', localTitle.value);
  listTitleFocused.value = false;
  localTitle.value = '';
}

const numberOfPointsLabel = (num: number) => {
  return num === 1 ? translate('onePoint') : translate('numberOfPoints', { num });
};
const totalEstimatePoints = () => {
  const currentProjectId = store.getters.getCurrentProjectId;
  const selectedEstimateSystem = store.getters.getEstimateSystemForProject(currentProjectId) || '';
  if (!selectedEstimateSystem) return '';
  const { values = [], type = '' } = constants.estimateSystems[selectedEstimateSystem] || {};
  if (type !== 'points' || !values.length) return '';
  const total = props.listCards.reduce((acc: number, cardId) => {
    const { estimate = 0 } = store.getters.getCards[cardId as string] || {};
    return estimate ? acc + values[estimate - 1] : acc;
  }, 0);
  return total ? `(${numberOfPointsLabel(Number(total))})` : '';
};
function totalEstimatePointsTooltipContent() {
  const iconStyle = getCustomIconStyle(iconPath('clock'));
  const icon = document.createElement('em');
  icon.classList.add('custom-icons', 'h-3', 'w-3', 'bg-attention-icon', 'mr-1');
  Object.assign(icon.style, iconStyle);
  const numOfCardsWithEstimate = props.listCards.reduce((acc: number, cardId) => {
    const { estimate = 0 } = store.getters.getCards[cardId as string] || {};
    return estimate ? acc + 1 : acc;
  }, 0);
  const text = translate('totalEstimatePointsTooltipContent', {
    totalPoints: totalEstimatePoints(),
    numOfCards: numOfCardsWithEstimate,
  });
  return `${icon.outerHTML} ${text} ${
    numOfCardsWithEstimate === 1
      ? translate('card').toLowerCase()
      : translate('cards').toLowerCase()
  }`;
}

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["list-header usn pt order-none", [
      { 'header-focused': listTitleFocused.value },
      { 'self-start': props.isCardsLayoutTimeline },
      { 'board-list-actions-dropdown-shown': actionsDropdownIsShown.value },
    ]]),
    style: _normalizeStyle(props.headerFooterStyle),
    onMouseenter: headerMouseEnter,
    onMouseleave: headerMouseOut,
    onMousedown: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(isEditable) && _unref(isEditable)(...args)))
  }, [
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
      (props.expandable)
        ? (_openBlock(), _createBlock(_unref(ToggleExpandButton), {
            key: 0,
            class: "w-5 h-5 p-0",
            expanded: isExpanded.value,
            onUpdate: onUpdateExpanded
          }, null, 8, ["expanded"]))
        : _createCommentVNode("", true),
      _cache[5] || (_cache[5] = _createTextVNode()),
      (props.showStatusIconPicker)
        ? _withDirectives((_openBlock(), _createBlock(_unref(StatusIconPicker), {
            key: 1,
            "status-icon": listStatusIcon.value,
            "status-color": listStatusColor.value,
            "selected-status": { id: _unref(list).behavior, color: listStatusColor.value },
            "list-id": listIdRef.value,
            "disable-status-edit": props.disableStatusIconPicker,
            onStatusItemClick: handleStatusItemClick,
            onColorSelect: handleColorSelect
          }, null, 8, ["status-icon", "status-color", "selected-status", "list-id", "disable-status-edit"])), [
            [_directive_tooltip, {
          content: _unref(translate)('editStatus'),
          onShow: () => !props.disableStatusIconPicker,
          placement: 'top',
        }]
          ])
        : _createCommentVNode("", true),
      _cache[6] || (_cache[6] = _createTextVNode()),
      (groupByAssignee.value && !isUnassignedListId.value)
        ? (_openBlock(), _createBlock(_unref(UserAvatar), {
            key: 2,
            class: "mr-2 cursor-default",
            member: _unref(list)
          }, null, 8, ["member"]))
        : _createCommentVNode("", true),
      _cache[7] || (_cache[7] = _createTextVNode()),
      (groupByEpic.value)
        ? (_openBlock(), _createBlock(_unref(ResourceIcon), {
            key: 3,
            src: listStatusIcon.value,
            color: listStatusColor.value,
            type: _unref(list)?.type || ''
          }, null, 8, ["src", "color", "type"]))
        : _createCommentVNode("", true),
      _cache[8] || (_cache[8] = _createTextVNode()),
      (!listTitleFocused.value)
        ? _withDirectives((_openBlock(), _createElementBlock("div", {
            key: 4,
            class: _normalizeClass(["list-title usn", { 'group-by': !groupByStatus.value || isSprint.value, 'safari-hidden': _unref(isSafari) }]),
            onClick: _withModifiers(focusListTitle, ["exact"])
          }, [
            _createTextVNode(_toDisplayString(_unref(list).title), 1)
          ], 2)), [
            [_directive_tooltip, {
          content: _unref(translate)('editName'),
          onShow: () => !props.disableStatusIconPicker,
          placement: 'top',
        }]
          ])
        : _createCommentVNode("", true),
      _cache[9] || (_cache[9] = _createTextVNode()),
      (listTitleFocused.value)
        ? _withDirectives((_openBlock(), _createElementBlock("input", {
            key: 5,
            ref_key: "listTitleInput",
            ref: listTitleInput,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((listTitle).value = $event)),
            type: "text",
            class: "list-title",
            contenteditable: "true",
            onKeyup: _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (handleEnterKey($event)), ["stop","prevent"]), ["enter"])),
            onKeydown: _withKeys(_withModifiers(closeTitleEditInput, ["stop"]), ["esc"]),
            onMousedown: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(focusElementHandler) && _unref(focusElementHandler)(...args))),
            onMouseup: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(blurElementHandler) && _unref(blurElementHandler)(...args)))
          }, null, 40, _hoisted_2)), [
            [_vModelText, listTitle.value],
            [_directive_click_outside, handleInputBlur]
          ])
        : _createCommentVNode("", true),
      _cache[10] || (_cache[10] = _createTextVNode()),
      (listTitleFocused.value)
        ? (_openBlock(), _createBlock(_unref(ButtonCustom), {
            key: 6,
            id: "discard-button",
            type: 'important-custom',
            variation: 'extra-small',
            class: "pt usn group bg-important-bg hover:bg-important-hover border-important-border ml-2",
            "custom-icon-name": 'x',
            "no-label": true,
            onClick: closeTitleEditInput
          }))
        : _createCommentVNode("", true),
      _cache[11] || (_cache[11] = _createTextVNode()),
      (listTitleFocused.value)
        ? (_openBlock(), _createBlock(_unref(ButtonCustom), {
            key: 7,
            variation: 'extra-small',
            type: 'important-custom',
            "custom-icon-name": 'check',
            class: "pt usn second group bg-important-bg hover:bg-important-hover border-important-border",
            "no-label": true
          }))
        : _createCommentVNode("", true),
      _cache[12] || (_cache[12] = _createTextVNode()),
      (__props.listCards && !listTitleFocused.value)
        ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_3, [
            _createTextVNode(_toDisplayString(__props.listCards.length), 1)
          ])), [
            [_directive_tooltip, {
          content: _unref(translate)('numberOfCards'),
          placement: 'top',
          onShow: () => !isInEpicBoard.value,
        }]
          ])
        : _createCommentVNode("", true),
      _cache[13] || (_cache[13] = _createTextVNode()),
      (!listTitleFocused.value && totalEstimatePoints)
        ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_4, [
            _createTextVNode(_toDisplayString(totalEstimatePoints()), 1)
          ])), [
            [_directive_tooltip, {
          content: totalEstimatePointsTooltipContent,
          placement: 'bottom',
          allowHTML: true,
        }]
          ])
        : _createCommentVNode("", true)
    ])), [
      [_directive_tooltip, {
        content: numberOfCardsTooltipContent.value,
        placement: 'top-start',
        onShow: () => !!isInEpicBoard.value,
        offset: [0, -5],
      }]
    ]),
    _cache[14] || (_cache[14] = _createTextVNode()),
    (showListActionsButton.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (!listTitleFocused.value && !readOnly.value && !isRolledoverList.value)
            ? (_openBlock(), _createBlock(_unref(BoardListActions), {
                key: 0,
                list: _unref(list),
                onRenameList: focusListTitle
              }, null, 8, ["list"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ], 38))
}
}

})